import React from 'react';
import { DatePickerInput, TextInput } from '@tassomutuo-backoffice/ui';

import { ManagedLeadAdvancementField } from '../../../services/api/managed-leads/managedLeads';
import { managedLeadCurrencyInput } from '../managedLeadCurrency';
import {
  advancementDateFromDate,
  advancementDateToDate,
  isSupportedAdvancementKind,
  ManagedLeadAdvancementErrors,
  ManagedLeadAdvancementValues,
} from '../managedLeadAdvancementData';

interface ManagedLeadAdvancementFieldsProps {
  fields: ManagedLeadAdvancementField[];
  values: ManagedLeadAdvancementValues;
  errors: ManagedLeadAdvancementErrors;
  disabled?: boolean;
  onChangeValue: (key: string, value: string) => void;
}

/**
 * The Advancement data the selected stage asks for, rendered in the order the
 * policy sent it and with the inputs Mutui already uses for the same kinds of
 * datum. A descriptor this release cannot render is left out here: the section
 * around it refuses the whole submission rather than let the operator save a
 * stage whose required data is missing.
 */
const ManagedLeadAdvancementFields = ({
  fields,
  values,
  errors,
  disabled = false,
  onChangeValue,
}: ManagedLeadAdvancementFieldsProps) => (
  <>
    {fields
      .filter((field) => isSupportedAdvancementKind(field.kind))
      .map((field) => {
        const value = values[field.key] ?? '';
        const helperText = errors[field.key] ?? null;
        const error = Boolean(helperText);

        if (field.kind === 'date') {
          return (
            <DatePickerInput
              key={field.key}
              label={field.label}
              value={advancementDateToDate(value)}
              disabled={disabled}
              // An appointment can be confirmed after it happened and a
              // liquidation date can be corrected long afterwards: the policy,
              // not the calendar, decides what is acceptable.
              disablePast={false}
              onChangeValue={(date: Date) =>
                onChangeValue(field.key, advancementDateFromDate(date))
              }
              error={error}
              helperText={helperText}
            />
          );
        }

        return (
          <TextInput
            key={field.key}
            // `time` is the native control Mutui already uses for an
            // appointment hour: it reads and writes `HH:mm` with no parsing of
            // ours in between.
            type={field.kind === 'time' ? 'time' : 'text'}
            label={field.label}
            showEuroIcon={field.kind === 'currency'}
            value={value}
            disabled={disabled}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChangeValue(
                field.key,
                field.kind === 'currency'
                  ? managedLeadCurrencyInput(event.target.value)
                  : event.target.value
              )
            }
            error={error}
            helperText={helperText}
          />
        );
      })}
  </>
);

export default ManagedLeadAdvancementFields;
